/**
 * src/hooks/usePaymentStatus.js
 *
 * Polls /api/invoices-payment-status for the calling tenant and returns
 * whether any invoices are overdue. Available to all authenticated users
 * (not just admins) so the app shell can show an account-wide banner.
 *
 * Does not return financial details (amounts, invoice numbers) — only the
 * boolean status and how many days the oldest invoice is overdue. This
 * respects BILL-B-001 / BILL-B-005 while enabling a tenant-wide warning.
 *
 * Polling interval: every 5 minutes while the session is active. The
 * status changes only when an operator marks an invoice paid, so frequent
 * polling is unnecessary.
 */

import { useState, useEffect } from 'react';
import { azureGet } from '../api/azureClient.js';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * @param {string|null} tenantId   — raw tenant id; pass null/undefined to disable
 * @param {object|null} session    — current auth session; pass null when logged out
 * @returns {{ hasOverdue: boolean, overdueCount: number, oldestOverdueDays: number|null, loading: boolean }}
 */
export function usePaymentStatus(tenantId, session) {
  const [status, setStatus] = useState({
    hasOverdue: false,
    overdueCount: 0,
    oldestOverdueDays: null,
    loading: true,
  });

  useEffect(() => {
    if (!tenantId || !session) {
      setStatus((s) => ({ ...s, loading: false }));
      return;
    }

    // isCurrent guards against stale-tenant races: if tenantId/session changes
    // while a request is in flight, the cleanup sets isCurrent=false so the
    // resolved data is discarded. Also prevents setState after unmount.
    let isCurrent = true;

    async function poll() {
      try {
        const data = await azureGet('/api/invoices-payment-status');
        if (!isCurrent) return;
        setStatus({
          hasOverdue: !!data?.hasOverdue,
          overdueCount: data?.overdueCount ?? 0,
          oldestOverdueDays: data?.oldestOverdueDays ?? null,
          loading: false,
        });
      } catch (err) {
        if (!isCurrent) return;
        // Network / auth errors — fail silently; don't block the app.
        if (import.meta.env.DEV) console.warn('[usePaymentStatus] poll failed:', err);
        setStatus((s) => ({ ...s, loading: false }));
      }
    }

    setStatus((s) => ({ ...s, loading: true }));
    poll();
    const timer = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      isCurrent = false;
      clearInterval(timer);
    };
  }, [tenantId, session]);

  return status;
}
